import { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import {
  ArrowLeft,
  User,
  Home,
  Shield,
  Calendar,
  CheckCircle,
  XCircle,
  AlertTriangle,
  StickyNote,
} from "lucide-react";
import { toast } from "sonner";

import { StatusBadge } from "@/components/admin/StatusBadge";
import { ConfirmModal } from "@/components/admin/ConfirmModal";
import { NightCapBar } from "@/components/admin/NightCapBar";
import { getBookingById } from "@/lib/admin/queries";
import {
  approveBooking,
  denyBooking,
  cancelBooking,
  overrideNightCap,
  updateUnit,
} from "@/lib/admin/actions";
import { useAuth } from "@/contexts/AuthContext";

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import type { Booking, BookingStatus } from "@/types/database";

// ─── Types ────────────────────────────────────────────────────────────────────

type BookingDetail = Booking & {
  unit?: {
    id: string;
    unit_number: string;
    organization_id: string;
    notes?: string | null;
    organization?: { id: string; name: string; address?: string | null } | null;
  } | null;
  host?: {
    id: string;
    nights_used: number;
    night_limit: number;
    status: string;
    registration_number?: string | null;
    profile?: {
      id: string;
      full_name: string;
      email: string;
      phone?: string | null;
    } | null;
  } | null;
};

type StatusAction =
  | { type: "approve" }
  | { type: "deny"; reason: string }
  | { type: "cancel" }
  | { type: "force_approve"; reason: string }
  | { type: "force_deny"; reason: string }
  | { type: "override_cap"; reason: string };

// ─── Helpers ──────────────────────────────────────────────────────────────────

function DetailRow({
  label,
  value,
}: {
  label: string;
  value?: string | number | null | React.ReactNode;
}) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-start gap-0.5 sm:gap-4 py-2 border-b border-gray-100 last:border-0">
      <dt className="text-xs font-semibold text-gray-500 uppercase tracking-wide sm:w-44 shrink-0 whitespace-nowrap">
        {label}
      </dt>
      <dd className="text-sm text-gray-900">
        {value ?? <span className="text-gray-400">—</span>}
      </dd>
    </div>
  );
}

const STATUS_STEPS: BookingStatus[] = [
  "pending",
  "approved",
  "completed",
];

function StatusTimeline({ current }: { current: BookingStatus }) {
  const isDenied = current === "blocked";
  const isCancelled = current === "cancelled";

  if (isDenied || isCancelled) {
    return (
      <div className="flex items-center gap-2 py-3">
        <div className="h-8 w-8 rounded-full bg-red-100 flex items-center justify-center">
          <XCircle className="h-4 w-4 text-red-600" />
        </div>
        <span className="text-sm font-medium text-red-700">
          {isDenied ? "Denied" : "Cancelled"}
        </span>
      </div>
    );
  }

  const currentIdx = STATUS_STEPS.indexOf(current as BookingStatus);

  return (
    <div className="flex items-center gap-0">
      {STATUS_STEPS.map((step, idx) => {
        const done = idx <= currentIdx;
        const active = idx === currentIdx;
        return (
          <div key={step} className="flex items-center">
            <div className="flex flex-col items-center">
              <div
                className={`h-8 w-8 rounded-full flex items-center justify-center transition-colors ${
                  done
                    ? "bg-green-100"
                    : "bg-gray-100"
                }`}
              >
                {done ? (
                  <CheckCircle
                    className={`h-4 w-4 ${active ? "text-green-600" : "text-green-500"}`}
                  />
                ) : (
                  <div className="h-2 w-2 rounded-full bg-gray-300" />
                )}
              </div>
              <span
                className={`text-xs mt-1 capitalize whitespace-nowrap ${
                  done ? "text-green-700 font-medium" : "text-gray-400"
                }`}
              >
                {step}
              </span>
            </div>
            {idx < STATUS_STEPS.length - 1 && (
              <div
                className={`h-0.5 w-10 mx-1 mb-5 ${done && idx < currentIdx ? "bg-green-400" : "bg-gray-200"}`}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function BookingDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Action states
  const [denyReason, setDenyReason] = useState("");
  const [forceReason, setForceReason] = useState("");
  const [overrideReason, setOverrideReason] = useState("");
  const [pendingAction, setPendingAction] = useState<StatusAction | null>(null);
  const [adminNotes, setAdminNotes] = useState("");
  const [notesSaved, setNotesSaved] = useState(false);

  // ─── Query ────────────────────────────────────────────────────────────────

  const {
    data: result,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["admin", "booking", id],
    queryFn: () => getBookingById(id!),
    enabled: !!id,
  });

  const booking = result?.data as BookingDetail | null | undefined;

  // ─── Mutations ────────────────────────────────────────────────────────────

  function invalidateBooking() {
    queryClient.invalidateQueries({ queryKey: ["admin", "booking", id] });
    queryClient.invalidateQueries({ queryKey: ["admin", "bookings"] });
  }

  const approveMutation = useMutation({
    mutationFn: () => approveBooking(id!, user!.id),
    onSuccess: () => {
      toast.success("Booking approved");
      setPendingAction(null);
      invalidateBooking();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const denyMutation = useMutation({
    mutationFn: (reason: string) => denyBooking(id!, reason, user!.id),
    onSuccess: () => {
      toast.success("Booking denied");
      setPendingAction(null);
      invalidateBooking();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const cancelMutation = useMutation({
    mutationFn: () => cancelBooking(id!, user!.id),
    onSuccess: () => {
      toast.success("Booking cancelled");
      setPendingAction(null);
      invalidateBooking();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const overrideMutation = useMutation({
    mutationFn: (reason: string) => overrideNightCap(id!, reason, user!.id),
    onSuccess: () => {
      toast.success("Night cap overridden and booking approved");
      setPendingAction(null);
      invalidateBooking();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const saveNotesMutation = useMutation({
    mutationFn: () => {
      if (!booking?.unit?.id) throw new Error("No unit");
      return updateUnit(
        booking.unit.id,
        { notes: `[Admin ${format(new Date(), "yyyy-MM-dd")}] ${adminNotes}\n${booking.unit?.notes ?? ""}` },
        user!.id
      );
    },
    onSuccess: () => {
      toast.success("Notes saved");
      setNotesSaved(true);
      invalidateBooking();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  // ─── Confirm dispatch ─────────────────────────────────────────────────────

  function handleConfirmAction() {
    if (!pendingAction) return;
    switch (pendingAction.type) {
      case "approve": approveMutation.mutate(); break;
      case "deny": denyMutation.mutate(pendingAction.reason); break;
      case "cancel": cancelMutation.mutate(); break;
      case "force_approve": approveMutation.mutate(); break;
      case "force_deny": denyMutation.mutate(pendingAction.reason); break;
      case "override_cap": overrideMutation.mutate(pendingAction.reason); break;
    }
  }

  const isMutating =
    approveMutation.isPending ||
    denyMutation.isPending ||
    cancelMutation.isPending ||
    overrideMutation.isPending;

  // ─── Derived data ─────────────────────────────────────────────────────────

  const host = booking?.host;
  const unit = booking?.unit;
  const nightsBefore = host?.nights_used ?? 0;
  const nightsThisBooking = booking?.nights ?? 0;
  const nightCap = host?.night_limit ?? 0;
  const nightsAfter = nightsBefore + nightsThisBooking;
  const exceedsCap = nightsAfter > nightCap;

  const confirmDescription = (): string => {
    if (!pendingAction) return "";
    switch (pendingAction.type) {
      case "approve": return "Approve this booking? The host's night counter will be incremented.";
      case "deny": return `Deny this booking with reason: "${pendingAction.reason}"?`;
      case "cancel": return "Cancel this approved booking? This action is logged.";
      case "force_approve": return `Force-approve this booking with reason: "${pendingAction.reason}"?`;
      case "force_deny": return `Force-deny this booking with reason: "${pendingAction.reason}"?`;
      case "override_cap": return `Override the night cap and approve this booking? Reason: "${pendingAction.reason}"`;
    }
  };

  // ─── Loading state ────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className="p-6 space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-4">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-40 w-full rounded-xl" />
            ))}
          </div>
          <div className="space-y-4">
            <Skeleton className="h-48 w-full rounded-xl" />
            <Skeleton className="h-32 w-full rounded-xl" />
          </div>
        </div>
      </div>
    );
  }

  if (error || !booking) {
    return (
      <div className="p-6">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>
        <Card>
          <CardContent className="py-12 text-center">
            <AlertTriangle className="h-8 w-8 text-amber-500 mx-auto mb-3" />
            <p className="text-gray-600">Booking not found or failed to load.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/admin/bookings")}
          className="shrink-0"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Bookings
        </Button>
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-bold text-gray-900 truncate">
            Booking #{booking.id.slice(0, 8)}
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Submitted {format(new Date(booking.created_at), "MMM d, yyyy 'at' h:mm a")}
          </p>
        </div>
        <StatusBadge
          status={
            booking.status === "blocked"
              ? "rejected"
              : booking.status
          }
        />
      </div>

      {/* Two-column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* LEFT: 2/3 */}
        <div className="lg:col-span-2 space-y-4">
          {/* Guest Info Card */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <User className="h-4 w-4 text-gray-400" />
                Guest Information
              </CardTitle>
            </CardHeader>
            <CardContent>
              <dl>
                <DetailRow label="Guest Name" value={booking.guest_name} />
                <DetailRow label="Email" value={booking.guest_email} />
                <DetailRow label="Phone" value={booking.guest_phone} />
                <DetailRow
                  label="Guest Count"
                  value={`${booking.guest_count} guest${booking.guest_count !== 1 ? "s" : ""}`}
                />
                <DetailRow
                  label="Check-in"
                  value={format(new Date(booking.check_in), "EEEE, MMM d, yyyy")}
                />
                <DetailRow
                  label="Check-out"
                  value={format(new Date(booking.check_out), "EEEE, MMM d, yyyy")}
                />
                <DetailRow
                  label="Nights"
                  value={booking.nights}
                />
                <DetailRow
                  label="Platform"
                  value={booking.platform
                    ? booking.platform.replace(/_/g, ".").replace(/\b\w/g, (c) => c.toUpperCase())
                    : undefined}
                />
                <DetailRow
                  label="Booking Ref"
                  value={booking.booking_reference}
                />
                <DetailRow
                  label="Purpose"
                  value={booking.purpose
                    ? booking.purpose.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
                    : undefined}
                />
                <DetailRow label="Notes" value={booking.booking_notes} />
              </dl>
            </CardContent>
          </Card>

          {/* Building + Unit Card */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Home className="h-4 w-4 text-gray-400" />
                Building &amp; Unit
              </CardTitle>
            </CardHeader>
            <CardContent>
              <dl>
                <DetailRow
                  label="Building"
                  value={
                    unit?.organization ? (
                      <Link
                        to={`/admin/buildings/${unit.organization.id}`}
                        className="text-blue-600 hover:text-blue-700 font-medium"
                      >
                        {unit.organization.name}
                      </Link>
                    ) : undefined
                  }
                />
                <DetailRow label="Address" value={unit?.organization?.address} />
                <DetailRow label="Unit #" value={unit?.unit_number} />
              </dl>
            </CardContent>
          </Card>

          {/* Host Card */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Shield className="h-4 w-4 text-gray-400" />
                Host
              </CardTitle>
            </CardHeader>
            <CardContent>
              <dl>
                <DetailRow label="Host Name" value={host?.profile?.full_name} />
                <DetailRow label="Email" value={host?.profile?.email} />
                <DetailRow label="Phone" value={host?.profile?.phone} />
                <DetailRow
                  label="Registration #"
                  value={host?.registration_number}
                />
                <DetailRow
                  label="Status"
                  value={
                    host ? <StatusBadge status={host.status} size="sm" /> : undefined
                  }
                />
              </dl>
            </CardContent>
          </Card>

          {/* Status Timeline */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Calendar className="h-4 w-4 text-gray-400" />
                Status Timeline
              </CardTitle>
            </CardHeader>
            <CardContent>
              <StatusTimeline current={booking.status} />
            </CardContent>
          </Card>

          {/* Admin Notes */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <StickyNote className="h-4 w-4 text-gray-400" />
                Internal Admin Notes
                <span className="text-xs text-gray-400 font-normal">(admin-only)</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {unit?.notes && (
                <div className="rounded-md bg-gray-50 border border-gray-200 p-3 text-sm text-gray-700 whitespace-pre-wrap font-mono">
                  {unit.notes}
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="admin-notes">Add note</Label>
                <Textarea
                  id="admin-notes"
                  rows={3}
                  value={adminNotes}
                  onChange={(e) => { setAdminNotes(e.target.value); setNotesSaved(false); }}
                  placeholder="Internal admin observations, flags, or follow-up actions…"
                />
              </div>
              <div className="flex justify-end">
                <Button
                  size="sm"
                  variant="outline"
                  className="whitespace-nowrap"
                  disabled={!adminNotes.trim() || saveNotesMutation.isPending}
                  onClick={() => saveNotesMutation.mutate()}
                >
                  {notesSaved ? "Saved" : saveNotesMutation.isPending ? "Saving…" : "Save Note"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* RIGHT: 1/3 */}
        <div className="space-y-4">
          {/* Status & Actions Card */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Status &amp; Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">Current status:</span>
                <StatusBadge
                  status={booking.status === "blocked" ? "rejected" : booking.status}
                />
              </div>

              {/* Primary actions */}
              {booking.status === "pending" && (
                <div className="flex gap-2">
                  <Button
                    className="flex-1 whitespace-nowrap"
                    onClick={() =>
                      setPendingAction({ type: "approve" })
                    }
                  >
                    <CheckCircle className="mr-2 h-4 w-4" />
                    Approve
                  </Button>
                  <Button
                    variant="destructive"
                    className="flex-1 whitespace-nowrap"
                    disabled={!denyReason.trim()}
                    onClick={() =>
                      setPendingAction({ type: "deny", reason: denyReason })
                    }
                  >
                    <XCircle className="mr-2 h-4 w-4" />
                    Deny
                  </Button>
                </div>
              )}
              {booking.status === "pending" && (
                <div className="space-y-1">
                  <Label htmlFor="deny-reason-sidebar" className="text-xs whitespace-nowrap">
                    Deny reason
                  </Label>
                  <Textarea
                    id="deny-reason-sidebar"
                    rows={2}
                    value={denyReason}
                    onChange={(e) => setDenyReason(e.target.value)}
                    placeholder="Required to deny…"
                    className="text-sm"
                  />
                </div>
              )}
              {booking.status === "approved" && (
                <Button
                  variant="outline"
                  className="w-full text-red-600 border-red-200 hover:bg-red-50 whitespace-nowrap"
                  onClick={() => setPendingAction({ type: "cancel" })}
                >
                  Cancel Booking
                </Button>
              )}

              <Separator />

              {/* Admin Override section */}
              <div className="space-y-3">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Admin Override
                </p>
                <Textarea
                  rows={2}
                  value={forceReason}
                  onChange={(e) => setForceReason(e.target.value)}
                  placeholder="Override reason (required)…"
                  className="text-sm"
                />
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className="flex-1 text-green-700 border-green-200 hover:bg-green-50 whitespace-nowrap"
                    disabled={!forceReason.trim()}
                    onClick={() =>
                      setPendingAction({ type: "force_approve", reason: forceReason })
                    }
                  >
                    Force Approve
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="flex-1 text-red-600 border-red-200 hover:bg-red-50 whitespace-nowrap"
                    disabled={!forceReason.trim()}
                    onClick={() =>
                      setPendingAction({ type: "force_deny", reason: forceReason })
                    }
                  >
                    Force Deny
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Night Cap Impact Card */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Night Cap Impact</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <NightCapBar used={nightsBefore} cap={nightCap} showLabel />
              <dl className="space-y-1.5 text-sm">
                <DetailRow
                  label="Nights before"
                  value={`${nightsBefore} / ${nightCap}`}
                />
                <DetailRow
                  label="This booking"
                  value={`+${nightsThisBooking} nights`}
                />
                <DetailRow
                  label="Nights after"
                  value={
                    <span className={exceedsCap ? "text-red-600 font-semibold" : "text-green-700 font-semibold"}>
                      {nightsAfter} / {nightCap}
                    </span>
                  }
                />
              </dl>
            </CardContent>
          </Card>

          {/* Compliance Check Card */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Compliance Check</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {exceedsCap ? (
                <>
                  <div className="flex items-center gap-2 rounded-md bg-red-50 border border-red-200 px-3 py-2.5">
                    <AlertTriangle className="h-4 w-4 text-red-600 shrink-0" />
                    <span className="text-sm font-medium text-red-700">
                      Exceeds night cap by {nightsAfter - nightCap} night
                      {nightsAfter - nightCap !== 1 ? "s" : ""}
                    </span>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="override-reason" className="text-xs whitespace-nowrap">
                      Override reason
                    </Label>
                    <Textarea
                      id="override-reason"
                      rows={2}
                      value={overrideReason}
                      onChange={(e) => setOverrideReason(e.target.value)}
                      placeholder="Justification for cap override…"
                      className="text-sm"
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      className="w-full text-amber-700 border-amber-200 hover:bg-amber-50 whitespace-nowrap"
                      disabled={!overrideReason.trim()}
                      onClick={() =>
                        setPendingAction({ type: "override_cap", reason: overrideReason })
                      }
                    >
                      Override &amp; Approve
                    </Button>
                  </div>
                </>
              ) : (
                <div className="flex items-center gap-2 rounded-md bg-green-50 border border-green-200 px-3 py-2.5">
                  <CheckCircle className="h-4 w-4 text-green-600 shrink-0" />
                  <span className="text-sm font-medium text-green-700">
                    Within night cap limits
                  </span>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Confirm Modal (shared for all actions) */}
      <ConfirmModal
        open={!!pendingAction}
        onClose={() => setPendingAction(null)}
        onConfirm={handleConfirmAction}
        title="Confirm Action"
        description={confirmDescription()}
        loading={isMutating}
        variant={
          pendingAction?.type === "approve" || pendingAction?.type === "force_approve"
            ? "warning"
            : "destructive"
        }
      />
    </div>
  );
}
